import React, { useState, useEffect, useRef } from 'react';

import ArrowIcon from './filters/ArrowIcon';
import SortIcon from './icons/SortIcon';

interface SortDropdownProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

const SortDropdownOptions: React.FC<{
  options: { value: string; label: string }[];
  selectedValue: string;
  onSelect: (value: string) => void;
  onClose: () => void;
}> = ({ options, onSelect, onClose, selectedValue }) => {
  const [tentativeValue, setTentativeValue] = useState(selectedValue);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchend', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, []);

  const focusOptionAt = (index: number): void => {
    const nextIndex = (index + options.length) % options.length;
    optionRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      className="absolute right-0 transform bg-white border rounded-3xl shadow-md z-50 border-1 border-gray-400 border-solid py-4 mt-0.5 text-sm text-gray-900 overflow-hidden"
      ref={dropdownRef}
      id="sort-dropdown-container"
      role="listbox"
      aria-label="Sort by"
    >
      {options.map((option, index) => (
        <div
          key={option.value}
          ref={(el) => {
            optionRefs.current[index] = el;
          }}
          className={`py-2 px-4 cursor-pointer hover:bg-blue-100 ${
            option.value === tentativeValue ? 'bg-blue-100' : ''
          }`}
          onClick={() => {
            onSelect(option.value);
            onClose();
          }}
          onMouseEnter={() => setTentativeValue(option.value)}
          onMouseLeave={() => setTentativeValue(selectedValue)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'Enter' || e.key === ' ') {
              onSelect(option.value);
              onClose();
              e.preventDefault();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              focusOptionAt(index + 1);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              focusOptionAt(index - 1);
            }
          }}
          role="option"
          // Intentionally selectedValue, not tentativeValue: aria-selected must
          // reflect the committed selection, while tentativeValue only drives
          // the hover/focus highlight below.
          aria-selected={option.value === selectedValue}
          tabIndex={0}
        >
          {option.label}
        </div>
      ))}
    </div>
  );
};
export default function SortDropdown({
  value,
  options,
  onChange,
}: SortDropdownProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleDropdownToggle = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleOptionSelect = (value: string) => {
    const selected = options.find((option) => option.value === value);
    if (selected) {
      onChange(value);
      closeDropdown();
    }
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  return (
    <div className="relative">
      <button
        className={`relative inline-flex items-center cursor-pointer rounded-xl p-4 text-sm font-semibold leading-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white border border-solid ${
          dropdownOpen
            ? 'bg-gray-800 text-white hover:bg-gray-900 border-transparent focus:border-transparent'
            : 'bg-white text-gray-900 hover:bg-gray-900 hover:text-white hover:border-transparent border-gray-250'
        }`}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={handleDropdownToggle}
        onKeyDown={(e) => {
          if (e.key === 'Escape') closeDropdown();
        }}
        aria-haspopup="listbox"
        aria-controls="sort-dropdown-container"
        aria-expanded={dropdownOpen}
        aria-labelledby="sort-by-info"
      >
        <span className="flex-grow flex items-center gap-2 mr-2">
          <SortIcon type={dropdownOpen || isHovering ? 'light' : 'dark'} />
          <span
            id="sort-by-info"
            className="flex-grow text-overflow-ellipsis whitespace-nowrap"
          >
            Sort By: {options.find((option) => option.value === value)?.label}
          </span>
        </span>
        <ArrowIcon
          isCollapsed={!dropdownOpen}
          invalidSelectionMessage={''}
          className={
            isHovering || dropdownOpen ? 'fill-white' : 'fill-gray-900'
          }
        />
      </button>

      {dropdownOpen && (
        <SortDropdownOptions
          options={options}
          selectedValue={value}
          onSelect={handleOptionSelect}
          onClose={closeDropdown}
        />
      )}
    </div>
  );
}
